import { Link } from 'react-router-dom'
import PageHero from '../components/PageHero'
import PageSeo from '../components/PageSeo'
import SiteFooter from '../components/SiteFooter'
import TopNav from '../components/TopNav'
import { buildOrganizationSchema, getAbsoluteUrl } from '../lib/seo'
import './ArticlePage.css'

const effectiveDate = '2026-05-04'
const lastUpdatedDate = '2026-05-04'

export default function TermsAndConditionsPage() {
  return (
    <div className="article-page">
      <PageSeo
        title="Terms and Conditions | Contrast Shower"
        description="Read the Terms and Conditions for the Contrast Shower app, including licence terms, subscriptions, privacy, liability, and contact details."
        canonical={getAbsoluteUrl('/terms-and-conditions')}
        image={getAbsoluteUrl('/images/blog-placeholder-basics.svg')}
        keywords={['contrast shower terms and conditions', 'terms and conditions', 'contrast shower terms']}
        schema={[buildOrganizationSchema()]}
      />

      <TopNav />

      <main>
        <article className="article-shell">
          <PageHero as="header" innerClassName="article-hero__inner">
            <nav className="article-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Terms and Conditions</span>
            </nav>

            <p className="eyebrow eyebrow--blog">Legal</p>
            <h1 className="article-hero__title heading-light">Terms &amp; Conditions</h1>
            <p className="article-hero__description p-intro">
              These Terms govern your use of the Contrast Shower mobile application.
            </p>

            <div className="article-hero__meta">
              <span>Effective date {effectiveDate}</span>
              <span>Last updated {lastUpdatedDate}</span>
            </div>
          </PageHero>

          <div className="container article-body-wrap">
            <div className="article-layout article-layout--single">
              <div className="article-content article-content--legal">
                <p className="article-intro">
                  These Terms and Conditions ("Terms") govern your use of the Contrast Shower mobile application (the "Application"), operated by Hatcher trading as Contrast Shower ("Service Provider", "we", "us" or "our"), an individual/sole trader based in QLD, Australia.
                </p>

                <section className="article-section">
                  <p>
                    By downloading or using the Application, you agree to be bound by these Terms. Please read them carefully before use. If you do not agree, do not use the Application.
                  </p>
                </section>

                <section className="article-section">
                  <h2>1. Licence, Ownership and Intellectual Property</h2>
                  <p>
                    We grant you a personal, revocable, non-transferable, non-exclusive licence to use the Application for your own lawful purposes, subject to these Terms.
                  </p>
                  <p>
                    All intellectual property rights in the Application - including but not limited to the source code, design, trademarks, database rights and content - remain the exclusive property of the Service Provider or our licensors. You are not granted any ownership rights.
                  </p>
                  <div className="article-list-block">
                    <ul>
                      <li>Copy, modify, adapt or create derivative works based on the Application</li>
                      <li>Attempt to extract or reverse-engineer the source code</li>
                      <li>Translate the Application into other languages without our written permission</li>
                      <li>Use our trademarks, logos or branding without prior written consent</li>
                    </ul>
                  </div>
                  <p>
                    The Application is distributed via the Apple App Store and Google Play Store. By downloading the Application from these platforms, you acknowledge that Apple Inc. and Google LLC are not parties to these Terms, their terms of service also apply to your use of the Application, and the platform providers have no obligation to provide maintenance or support for the Application.
                  </p>
                  <p>
                    The Application may incorporate third-party libraries, APIs or software development kits (SDKs), including RevenueCat for payment processing. Such third-party components may be subject to their own licence terms.
                  </p>
                </section>

                <section className="article-section">
                  <h2>2. Acceptable Use and Age Restrictions</h2>
                  <h3>2.1 No Account Required</h3>
                  <p>
                    The Application does not require you to create an account or provide personal login credentials. Subscription and payment management is handled through RevenueCat and your Apple or Google app store account.
                  </p>
                  <h3>2.2 Age Restrictions</h3>
                  <p>
                    The Application is intended for users aged 13 years and over. If you are under 18 years of age, you must have obtained your parent&apos;s or legal guardian&apos;s consent before using the Application. By using the Application, you confirm that you meet this requirement.
                  </p>
                  <p>
                    We do not knowingly collect personal information from children under 13. If you believe a child under 13 has used the Application without consent, please contact us at support@contrastshower.com.
                  </p>
                  <h3>2.3 Acceptable Use</h3>
                  <p>
                    You agree to use the Application only for lawful purposes and in a manner consistent with all applicable laws and regulations. You must not use the Application in any way that is unlawful, harmful, fraudulent or deceptive, attempt to gain unauthorised access to any part of the Application or its related systems, introduce malware, viruses or other malicious code, interfere with the operation or security of the Application, or jailbreak or root your device to circumvent the Application&apos;s security features.
                  </p>
                </section>

                <section className="article-section">
                  <h2>3. Payments, Free Trial, Subscriptions and Refunds</h2>
                  <h3>3.1 Freemium Model and Free Trial</h3>
                  <p>
                    The Application is offered as a freemium service with a free trial period, after which continued access to premium features requires a paid subscription. Details of the trial period, including its length and what is included, are displayed within the Application at the time of sign-up.
                  </p>
                  <p>
                    At the end of your free trial, access to premium features will cease unless you choose to purchase a subscription. There is no automatic charge at the end of the trial - you must actively opt in to a paid plan to continue using premium features.
                  </p>
                  <h3>3.2 Pricing and Billing</h3>
                  <p>
                    Prices for subscriptions and in-app purchases are set by us but are displayed in the user&apos;s local currency, as determined by the applicable app store based on the user&apos;s account region.
                  </p>
                  <p>
                    Subscription fees are billed on a recurring basis (monthly or annually, as selected) through your Apple App Store or Google Play account via RevenueCat. Payment will be charged to your app store account at the time of purchase confirmation.
                  </p>
                  <p>
                    Actual pricing may vary between regions and may not correspond directly to currency exchange rates due to app store pricing tiers, taxes, and other factors. We reserve the right to change our pricing at any time. Where required by applicable law or platform rules, we will provide reasonable notice of any price changes before they take effect.
                  </p>
                  <h3>3.3 Auto-Renewal and Cancellation</h3>
                  <p>
                    Once you have opted in to a paid subscription, it will automatically renew at the end of each billing period unless cancelled. To cancel, you must do so through your Apple App Store or Google Play account settings before the renewal date. Cancellation takes effect at the end of the current billing period - you will retain access to premium features until that date.
                  </p>
                  <h3>3.4 Refunds</h3>
                  <p>
                    Payments are processed by Apple or Google through their respective app stores. Refund requests are subject to Apple&apos;s or Google&apos;s refund policies. We are unable to issue refunds directly.
                  </p>
                  <p>
                    Nothing in these Terms limits your rights under the Australian Consumer Law (ACL). If the Application has a major failure, you may be entitled to a remedy under the ACL, including a refund.
                  </p>
                </section>

                <section className="article-section">
                  <h2>4. Liability, Warranties and Service Availability</h2>
                  <h3>4.1 Service Availability</h3>
                  <p>
                    We strive to keep the Application operational and up to date. However, we do not guarantee uninterrupted or error-free access. The Application may be temporarily unavailable due to maintenance, updates, technical issues or circumstances beyond our control.
                  </p>
                  <p>
                    Some features of the Application require an active internet connection (Wi-Fi or mobile data). We are not responsible if the Application does not perform at full capacity due to lack of internet access or exhaustion of your data allowance.
                  </p>
                  <h3>4.2 No Warranties</h3>
                  <p>
                    To the maximum extent permitted by law, we provide the Application "as is" and make no warranties - express or implied - regarding fitness for a particular purpose, accuracy, reliability or availability. We rely on third-party information to deliver certain features and cannot guarantee that all such information is current or error-free.
                  </p>
                  <p>
                    Nothing in these Terms excludes any consumer guarantee or other right you may have under the Australian Consumer Law that cannot be lawfully excluded.
                  </p>
                  <h3>4.3 Limitation of Liability</h3>
                  <p>
                    To the maximum extent permitted by law, our total liability to you for any loss or damage arising from your use of the Application is limited to the amount you paid for the Application in the 12 months preceding the event giving rise to the claim.
                  </p>
                  <p>
                    We are not liable for any indirect, incidental, consequential or special loss, including loss of data, loss of revenue or loss of profits, even if we have been advised of the possibility of such loss.
                  </p>
                  <div className="article-list-block">
                    <ul>
                      <li>Failure to use the Application because your device runs out of battery or storage</li>
                      <li>Data charges incurred from your mobile network provider, including roaming charges when using the Application outside your home country</li>
                      <li>Any loss arising from your reliance on third-party information provided through the Application</li>
                    </ul>
                  </div>
                </section>

                <section className="article-section">
                  <h2>5. Privacy, Data and Security</h2>
                  <p>
                    Your privacy matters to us. This section summarises how we handle data in connection with the Application. For full details, please refer to our <Link to="/privacy-policy">Privacy Policy</Link>.
                  </p>
                  <h3>5.1 Data We Collect</h3>
                  <p>The Application may collect and process data including:</p>
                  <div className="article-list-block">
                    <ul>
                      <li>Usage data and app analytics (e.g. session activity, feature usage)</li>
                      <li>Device information (e.g. operating system version, device type)</li>
                      <li>Subscription and purchase data (processed by RevenueCat, Apple and/or Google)</li>
                    </ul>
                  </div>
                  <p>
                    We do not collect account credentials, as the Application does not have its own login system.
                  </p>
                  <h3>5.2 Data Storage and Processing</h3>
                  <p>
                    Data may be stored and processed in Australia or overseas, including in the United States, via third-party service providers such as RevenueCat. We take reasonable steps to ensure these providers handle your data in accordance with applicable privacy laws.
                  </p>
                  <h3>5.3 Security</h3>
                  <p>
                    We implement reasonable technical and organisational measures to protect your data. However, no system is completely secure. You are responsible for maintaining the security of your device and keeping the Application up to date.
                  </p>
                  <p>
                    We strongly advise against jailbreaking or rooting your device, as this can compromise security features and may prevent the Application from functioning correctly.
                  </p>
                  <h3>5.4 Data Retention and Deletion</h3>
                  <p>
                    We retain your data for as long as necessary to provide the Application and comply with our legal obligations. If you cease using the Application and delete it from your device, data held by third-party processors (such as RevenueCat) will be subject to their own retention policies.
                  </p>
                  <h3>5.5 Data Breaches</h3>
                  <p>
                    In the event of a data breach affecting your personal information, we will notify you and the relevant authorities as required under the Notifiable Data Breaches scheme under the Privacy Act 1988 (Cth).
                  </p>
                </section>

                <section className="article-section">
                  <h2>6. Termination, Disputes and Changes to These Terms</h2>
                  <h3>6.1 Termination by Us</h3>
                  <p>
                    We may suspend or terminate your access to the Application at any time if you breach these Terms, we reasonably suspect fraudulent or unlawful activity, or we decide to cease operating or substantially modify the Application.
                  </p>
                  <p>
                    We may also discontinue the Application at any time. Unless required by law, we are not obliged to provide advance notice of termination. Upon termination, your licence to use the Application ends and you must delete it from your device.
                  </p>
                  <h3>6.2 Effect of Termination</h3>
                  <p>
                    Upon termination of your access: all rights granted to you under these Terms cease immediately; you must delete the Application from your device; and provisions of these Terms that by their nature should survive termination, including liability limitations and dispute resolution, will continue to apply.
                  </p>
                  <h3>6.3 Updates to the Application</h3>
                  <p>
                    We may update the Application from time to time. We recommend you keep the Application updated to ensure continued compatibility and access to security improvements. We do not guarantee that updates will always be made available or that the Application will remain compatible with your device&apos;s operating system.
                  </p>
                  <h3>6.4 Changes to These Terms</h3>
                  <p>
                    We may update these Terms periodically. When we do, we will revise the Last Updated date at the top of this document and notify you via the Application or by email where reasonably practicable. Your continued use of the Application after changes take effect constitutes your acceptance of the updated Terms. If you do not agree to the changes, you should stop using the Application and cancel any active subscription.
                  </p>
                  <h3>6.5 Governing Law and Disputes</h3>
                  <p>
                    These Terms are governed by the laws of Australia. You agree to submit to the non-exclusive jurisdiction of the courts of Australia for the resolution of any dispute arising under or in connection with these Terms.
                  </p>
                  <p>
                    We encourage you to contact us in the first instance to resolve any dispute informally. Most concerns can be addressed quickly through our support channel.
                  </p>
                </section>

                <section className="article-section">
                  <h2>7. Contact Us</h2>
                  <p>
                    If you have any questions, concerns or complaints about these Terms or the Application, please contact us at support@contrastshower.com.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  )
}
